import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import WarningRounded from "@mui/icons-material/WarningRounded";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { toastr } from "react-redux-toastr";
import { LOCATION_STATUS_CONST } from "@/config/constants";
import { locationManagementService } from "../services/locationManagementService";

type LocationStatusValue = string | number | null | undefined;

type LocationStatusPayload = {
  brand_id?: string | number;
  location_id?: string | number;
  location_status?: string | number;
  reopening_date?: string | null;
  closure_date?: string | null;
  ad_stop_date?: string | null;
};

type LocationStatusLocation = {
  fk_location_id?: string | number;
};

type StatusDateLimits = {
  min: string;
  max: string;
};

type StatusDateErrorParams = {
  status?: string;
  date?: string | null;
  required?: boolean;
  fieldLabel: string;
  maxDateOverride?: string;
};

type StatusDatePickerProps = {
  label: string;
  required?: boolean;
  status?: string;
  date?: string;
  dateError?: string;
  isSubmitting?: boolean;
  disabled?: boolean;
  maxDateOverride?: string;
  onDateChange: (_nextDate: string) => void;
  sx?: object;
};

type StatusChangeDialogProps = {
  open: boolean;
  status: string;
  date: string;
  dateError: string;
  adsStopDate: string;
  adsStopDateError: string;
  isSubmitting: boolean;
  onDateChange: (_nextDate: string) => void;
  onAdsStopDateChange: (_nextDate: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
  onExited: () => void;
};

type LocationStatusCellProps = {
  brandId?: string | number;
  location?: LocationStatusLocation;
  value?: LocationStatusValue;
};

const STATUS_STYLES = {
  [LOCATION_STATUS_CONST.OPEN.LABEL]: {
    backgroundColor: "var(--status-success-tint)",
    color: "var(--status-success)",
  },
  [LOCATION_STATUS_CONST.TEMPORARILY_CLOSED.LABEL]: {
    backgroundColor: "var(--status-warning-tint)",
    color: "var(--status-warning)",
  },
  [LOCATION_STATUS_CONST.CLOSED.LABEL]: {
    backgroundColor: "var(--status-error-tint)",
    color: "var(--status-error)",
  },
};

const getDisplayStatus = (status?: LocationStatusValue) =>
  LOCATION_STATUS_CONST.LABEL_BY_ID[status] ||
  status ||
  LOCATION_STATUS_CONST.DEFAULT;

const isStatusWithPopup = (status: string) =>
  LOCATION_STATUS_CONST.OPTIONS.includes(status);

const formatDateForInput = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const addDateParts = (
  date: Date,
  {
    days = 0,
    months = 0,
    years = 0,
  }: { days?: number; months?: number; years?: number }
) => {
  const nextDate = new Date(date);
  nextDate.setHours(0, 0, 0, 0);

  if (years) nextDate.setFullYear(nextDate.getFullYear() + years);
  if (months) nextDate.setMonth(nextDate.getMonth() + months);
  if (days) nextDate.setDate(nextDate.getDate() + days);

  return nextDate;
};

const getStatusDateLimits = (status?: string): StatusDateLimits => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return {
    min: formatDateForInput(addDateParts(today, { days: 1 })),
    max: formatDateForInput(
      addDateParts(
        today,
        status === LOCATION_STATUS_CONST.CLOSED.LABEL
          ? { months: 2 }
          : { years: 1 }
      )
    ),
  };
};

const getStatusDateError = ({
  status,
  date,
  required = false,
  fieldLabel,
  maxDateOverride,
}: StatusDateErrorParams) => {
  if (!date) {
    return required ? `${fieldLabel} is required` : "";
  }

  const { min, max } = getStatusDateLimits(status);
  const effectiveMax = maxDateOverride || max;

  if (date < min) return "Past dates are not allowed";
  if (date > effectiveMax) {
    if (fieldLabel === "Ads Stop Date" && maxDateOverride) {
      return "Ads Stop Date cannot be beyond Location Closure Date";
    }

    return status === LOCATION_STATUS_CONST.CLOSED.LABEL
      ? "Location Closure Date must be no more than 2 months from today"
      : "Reopening Date must be no more than 1 year from today";
  }

  return "";
};

const StatusDatePicker = ({
  label,
  required = false,
  status,
  date,
  dateError,
  isSubmitting,
  disabled = false,
  maxDateOverride,
  onDateChange,
  sx,
}: StatusDatePickerProps) => {
  const dateLimits = getStatusDateLimits(status);
  const maxDate = maxDateOverride || dateLimits.max;
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const pickerValue = date ? moment(date, "YYYY-MM-DD") : null;
  const isDisabled = isSubmitting || disabled;
  const openDatePicker = () => {
    if (isDisabled) return;
    setDatePickerOpen(true);
  };

  return (
    <Box sx={{ textAlign: "left", ...sx }}>
      <Typography
        sx={{ color: "var(--palette-text-primary)", fontSize: 14, mb: 1 }}
      >
        {label}
        {required ? (
          <Box component="span" sx={{ color: "var(--status-error)", ml: 0.5 }}>
            *
          </Box>
        ) : null}
      </Typography>
      <Box sx={{ position: "relative" }}>
        <DatePicker
          open={!isDisabled && datePickerOpen}
          onOpen={openDatePicker}
          onClose={() => setDatePickerOpen(false)}
          value={pickerValue}
          onChange={(nextDate) => {
            onDateChange(
              nextDate && nextDate.isValid()
                ? nextDate.format("YYYY-MM-DD")
                : ""
            );
          }}
          minDate={moment(dateLimits.min, "YYYY-MM-DD")}
          maxDate={moment(maxDate, "YYYY-MM-DD")}
          disabled={isDisabled}
          format="MM-DD-YYYY"
          slotProps={{
            textField: {
              fullWidth: true,
              error: Boolean(dateError),
              helperText: dateError,
              onClick: openDatePicker,
              inputProps: {
                readOnly: true,
                "aria-label": label,
              },
              InputProps: {
                readOnly: true,
              },
              sx: {
                "& .MuiPickersInputBase-root": {
                  height: 46,
                  borderRadius: "var(--radius-control)",
                  color: date
                    ? "var(--palette-text-primary)"
                    : "var(--neutral-600)",
                  cursor: isDisabled ? "default" : "pointer",
                },
                "& input": {
                  cursor: isDisabled ? "default" : "pointer",
                },
                "& .MuiPickersSectionList-root": {
                  opacity: date ? 1 : 0,
                },
              },
            },
            openPickerButton: {
              disabled: isDisabled,
              sx: {
                color: "var(--neutral-500)",
              },
            },
            popper: {
              sx: {
                zIndex: 99999,
              },
            },
          }}
        />
        {!date ? (
          <Box
            sx={{
              position: "absolute",
              top: 23,
              left: 14,
              transform: "translateY(-50%)",
              pointerEvents: "none",
              color: isDisabled ? "var(--neutral-500)" : "var(--neutral-600)",
              fontSize: 14,
            }}
          >
            Select Date
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

const StatusPill = ({ status }: { status: string }) => {
  const statusStyle =
    STATUS_STYLES[status] || STATUS_STYLES[LOCATION_STATUS_CONST.DEFAULT];

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        minHeight: 28,
        borderRadius: "var(--radius-round)",
        padding: "4px 10px",
        fontSize: 13,
        fontWeight: 500,
        lineHeight: "20px",
        whiteSpace: "nowrap",
        ...statusStyle,
      }}
    >
      {status}
    </span>
  );
};

const StatusSelect = ({
  status,
  onChange,
}: {
  status: string;
  onChange: (_nextStatus: string) => void;
}) => {
  const statusStyle =
    STATUS_STYLES[status] || STATUS_STYLES[LOCATION_STATUS_CONST.DEFAULT];

  return (
    <Select
      size="small"
      value={status}
      onChange={(event) => onChange(event.target.value)}
      onClick={(event) => event.stopPropagation()}
      displayEmpty
      sx={{
        minWidth:
          status === LOCATION_STATUS_CONST.TEMPORARILY_CLOSED.LABEL ? 148 : 70,
        height: 28,
        borderRadius: "var(--radius-control)",
        backgroundColor: statusStyle.backgroundColor,
        color: statusStyle.color,
        fontSize: 13,
        fontWeight: 500,
        lineHeight: "20px",
        "& .MuiSelect-select": {
          py: "4px",
          pl: "10px",
          pr: "28px !important",
        },
        "& .MuiOutlinedInput-notchedOutline": {
          border: 0,
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          border: 0,
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          border: 0,
        },
        "& .MuiSelect-icon": {
          color: statusStyle.color,
          right: 6,
        },
      }}
      MenuProps={{
        MenuListProps: {
          sx: {
            py: 0,
          },
        },
        PaperProps: {
          sx: {
            borderRadius: "var(--radius-container)",
            boxShadow: "var(--shadow-floating)",
            mt: 0.75,
            overflow: "hidden",
            "& .MuiMenuItem-root": {
              color: "var(--palette-text-primary)",
              fontSize: 13,
              minHeight: 36,
              px: 1.5,
              py: 0,
              "&.Mui-selected": {
                backgroundColor: "var(--primary-tint)",
                color: (theme) => theme.palette.primary.main,
                fontWeight: 700,
              },
              "&.Mui-selected.Mui-focusVisible, &.Mui-selected:hover": {
                backgroundColor: "var(--primary-tint-hover)",
              },
            },
          },
        },
      }}
    >
      {LOCATION_STATUS_CONST.OPTIONS.map((option) => (
        <MenuItem key={option} value={option}>
          {option}
        </MenuItem>
      ))}
    </Select>
  );
};

const StatusChangeDialog = ({
  open,
  status,
  date,
  dateError,
  adsStopDate,
  adsStopDateError,
  isSubmitting,
  onDateChange,
  onAdsStopDateChange,
  onCancel,
  onConfirm,
  onExited,
}: StatusChangeDialogProps) => {
  const isTemporary = status === LOCATION_STATUS_CONST.TEMPORARILY_CLOSED.LABEL;
  const isOpen = status === LOCATION_STATUS_CONST.OPEN.LABEL;
  const iconColor = isTemporary
    ? "var(--status-warning)"
    : "var(--status-error)";
  const buttonColor = isOpen
    ? "var(--status-success)"
    : isTemporary
      ? "var(--status-warning)"
      : "var(--status-error)";
  const dialogTitle = isOpen
    ? "Open Location"
    : isTemporary
      ? "Temporarily Closed?"
      : "Closed?";
  const dialogMessage = isOpen
    ? "The location will be marked as open and will be available in the system."
    : isTemporary
      ? "The location will be marked as temporarily closed until the date you add below."
      : "The location will be marked as permanently closed and will not be available in the system. A [CLOSED] tag will be added to the name.";
  const confirmLabel = isOpen
    ? "Open Location"
    : isTemporary
      ? "Close Temporarily"
      : "Close Location";

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onCancel}
      maxWidth="md"
      fullWidth
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor:
              "color-mix(in srgb, var(--palette-common-black) 35%, transparent)",
            backdropFilter: "blur(1px)",
          },
        },
        paper: {
          sx: {
            maxWidth: 700,
            borderRadius: "var(--radius-container)",
          },
        },
        transition: {
          onExited,
        },
      }}
    >
      <DialogContent
        sx={{
          px: { xs: 3, md: 5 },
          py: { xs: 3.5, md: 4 },
          textAlign: "center",
        }}
      >
        {!isOpen ? (
          <WarningRounded sx={{ color: iconColor, fontSize: 100, mb: 1.5 }} />
        ) : null}
        <Typography
          variant="h5"
          sx={{ color: "var(--palette-text-primary)", fontWeight: 700, mb: 2 }}
        >
          {dialogTitle}
        </Typography>
        <Typography
          sx={{ color: "var(--palette-text-primary)", fontSize: 15, mb: 2 }}
        >
          {dialogMessage}
        </Typography>

        {!isOpen ? (
          <LocalizationProvider
            dateAdapter={AdapterMoment}
            localeText={{
              fieldMonthPlaceholder: () => "",
              fieldDayPlaceholder: () => "",
              fieldYearPlaceholder: () => "",
            }}
          >
            <Box sx={{ mb: 5 }}>
              <StatusDatePicker
                label={isTemporary ? "Reopening Date" : "Location Closure Date"}
                required={!isTemporary}
                status={status}
                date={date}
                dateError={dateError}
                isSubmitting={isSubmitting}
                onDateChange={onDateChange}
                sx={{ mb: !isTemporary ? 2.5 : 0 }}
              />
              {!isTemporary ? (
                <StatusDatePicker
                  label="Ads Stop Date"
                  status={status}
                  date={adsStopDate}
                  dateError={adsStopDateError}
                  isSubmitting={isSubmitting}
                  disabled={!date}
                  maxDateOverride={date || undefined}
                  onDateChange={onAdsStopDateChange}
                />
              ) : null}
            </Box>
          </LocalizationProvider>
        ) : (
          <Box sx={{ mb: 5 }} />
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
            alignItems: "center",
          }}
        >
          <Button
            variant="text"
            onClick={onCancel}
            disabled={isSubmitting}
            sx={{
              color: "var(--palette-text-primary)",
              fontWeight: 500,
              minHeight: 45,
              textTransform: "none",
              width: "100%",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onConfirm}
            disabled={isSubmitting}
            sx={{
              backgroundColor: buttonColor,
              borderRadius: "var(--radius-control)",
              boxShadow: "none",
              fontWeight: 700,
              minHeight: 45,
              textTransform: "none",
              width: "100%",
              "&:hover": {
                backgroundColor: buttonColor,
                boxShadow: "none",
              },
            }}
          >
            {isSubmitting ? "Saving..." : confirmLabel}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const LocationStatusCell = ({
  brandId,
  location,
  value,
}: LocationStatusCellProps) => {
  const [currentStatus, setCurrentStatus] = useState(getDisplayStatus(value));
  const [pendingStatus, setPendingStatus] = useState("");
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [statusDate, setStatusDate] = useState("");
  const [statusDateError, setStatusDateError] = useState("");
  const [adsStopDate, setAdsStopDate] = useState("");
  const [adsStopDateError, setAdsStopDateError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setCurrentStatus(getDisplayStatus(value));
  }, [value]);

  const handleStatusUpdate = useCallback(
    async ({
      nextStatus,
      closureDate,
      adsStopDate,
    }: {
      nextStatus: string;
      closureDate?: string | null;
      adsStopDate?: string | null;
    }) => {
      const payload: LocationStatusPayload = {
        brand_id: brandId,
        location_id: location?.fk_location_id,
        location_status: LOCATION_STATUS_CONST.BY_LABEL[nextStatus],
      };

      if (nextStatus === LOCATION_STATUS_CONST.TEMPORARILY_CLOSED.LABEL) {
        payload.reopening_date = closureDate || null;
      }

      if (nextStatus === LOCATION_STATUS_CONST.CLOSED.LABEL) {
        payload.closure_date = closureDate || null;
        payload.ad_stop_date = adsStopDate || null;
      }

      const response =
        await locationManagementService.updateLocationStatus(payload);
      toastr.success(
        "Success",
        response?.data?.message || "Location status updated successfully."
      );
      setCurrentStatus(nextStatus);
    },
    [brandId, location?.fk_location_id]
  );

  const handleStatusSelect = useCallback(
    async (nextStatus) => {
      if (nextStatus === currentStatus) return;

      if (isStatusWithPopup(nextStatus)) {
        setPendingStatus(nextStatus);
        setIsStatusDialogOpen(true);
        setStatusDate("");
        setStatusDateError("");
        setAdsStopDate("");
        setAdsStopDateError("");
        return;
      }

      await handleStatusUpdate({
        nextStatus,
        closureDate: null,
      });
    },
    [currentStatus, handleStatusUpdate]
  );

  const handleDialogCancel = useCallback(() => {
    if (isSubmitting) return;
    setIsStatusDialogOpen(false);
  }, [isSubmitting]);

  const handleDialogExited = useCallback(() => {
    setPendingStatus("");
    setStatusDate("");
    setStatusDateError("");
    setAdsStopDate("");
    setAdsStopDateError("");
  }, []);

  const handleDialogConfirm = useCallback(async () => {
    if (!pendingStatus) return;

    if (pendingStatus !== LOCATION_STATUS_CONST.OPEN.LABEL) {
      const validationError = getStatusDateError({
        status: pendingStatus,
        date: statusDate,
        required: pendingStatus === LOCATION_STATUS_CONST.CLOSED.LABEL,
        fieldLabel:
          pendingStatus === LOCATION_STATUS_CONST.CLOSED.LABEL
            ? "Location Closure Date"
            : "Reopening Date",
      });

      if (validationError) {
        setStatusDateError(validationError);
        return;
      }

      const adsStopDateValidationError = getStatusDateError({
        status: pendingStatus,
        date: adsStopDate,
        fieldLabel: "Ads Stop Date",
        maxDateOverride: statusDate || undefined,
      });

      if (adsStopDateValidationError) {
        setAdsStopDateError(adsStopDateValidationError);
        return;
      }
    }

    setIsSubmitting(true);
    try {
      await handleStatusUpdate({
        nextStatus: pendingStatus,
        closureDate: statusDate || null,
        adsStopDate: adsStopDate || null,
      });
      setIsStatusDialogOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  }, [adsStopDate, handleStatusUpdate, pendingStatus, statusDate]);

  if (currentStatus === LOCATION_STATUS_CONST.CLOSED.LABEL) {
    return <StatusPill status={currentStatus} />;
  }

  return (
    <>
      <StatusSelect status={currentStatus} onChange={handleStatusSelect} />
      <StatusChangeDialog
        open={isStatusDialogOpen}
        status={pendingStatus}
        date={statusDate}
        dateError={statusDateError}
        adsStopDate={adsStopDate}
        adsStopDateError={adsStopDateError}
        isSubmitting={isSubmitting}
        onDateChange={(nextDate) => {
          setStatusDate(nextDate);
          setStatusDateError("");
          setAdsStopDate("");
          setAdsStopDateError("");
        }}
        onAdsStopDateChange={(nextDate) => {
          setAdsStopDate(nextDate);
          setAdsStopDateError("");
        }}
        onCancel={handleDialogCancel}
        onConfirm={handleDialogConfirm}
        onExited={handleDialogExited}
      />
    </>
  );
};

export default LocationStatusCell;
